// Server state for dashboards and schematics. Both share ownership, sharing, versions and
// templates, so the list, share and version hooks take the `kind` of document.
import type {
  Dashboard,
  DashboardDoc,
  DashboardSummary,
  DirectoryEntry,
  OkResponse,
  QueryResult,
  QuerySpec,
  ScopeBinding,
  Sharing,
  Signal,
  Template,
} from '@twinlink/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@web/lib/api';
import { downloadText } from '@web/lib/format';
import { orgKeys } from '@web/lib/org';
import { buildDashboardFile, dashboardFileName } from './transfer';

export type VizKind = 'dashboards' | 'schematics';
const base = (orgId: string) => `/orgs/${orgId}`;
export const vizKeys = {
  list: (orgId: string, kind: VizKind) => ['org', orgId, kind] as const,
  one: (orgId: string, kind: VizKind, id: string) => ['org', orgId, kind === 'dashboards' ? 'dashboard' : 'schematic', id] as const,
};
export const dashKeys = {
  list: (orgId: string) => vizKeys.list(orgId, 'dashboards'),
  one: (orgId: string, id: string) => vizKeys.one(orgId, 'dashboards', id),
};

export const useVizList = (orgId: string, kind: VizKind, enabled = true) =>
  useQuery({ queryKey: vizKeys.list(orgId, kind), queryFn: () => api<{ items: DashboardSummary[] }>(`${base(orgId)}/${kind}`).then((r) => r.items), enabled });
export const useDashboards = (orgId: string) => useVizList(orgId, 'dashboards');

export const useDashboard = (orgId: string, id: string | undefined) =>
  useQuery({ queryKey: dashKeys.one(orgId, id ?? ''), queryFn: () => api<Dashboard>(`${base(orgId)}/dashboards/${id}`), enabled: !!id, retry: false });

export const useTemplates = (orgId: string, enabled: boolean, kind: VizKind = 'dashboards') =>
  useQuery({
    queryKey: ['org', orgId, 'templates', kind],
    queryFn: () => api<{ items: Template[] }>(`${base(orgId)}/templates`, { query: { kind: kind === 'dashboards' ? 'dashboard' : 'schematic' } }).then((r) => r.items),
    enabled,
  });

export const useDirectory = (orgId: string, enabled: boolean) =>
  useQuery({ queryKey: ['org', orgId, 'directory'], queryFn: () => api<{ items: DirectoryEntry[] }>(`${base(orgId)}/directory`).then((r) => r.items), enabled });

export function useCreateViz(orgId: string, kind: VizKind) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; description?: string; fromTemplateId?: string; doc?: DashboardDoc; bind?: ScopeBinding }) => api<{ id: string; version: number }>(`${base(orgId)}/${kind}`, { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: vizKeys.list(orgId, kind) }),
  });
}
export const useCreateDashboard = (orgId: string) => useCreateViz(orgId, 'dashboards');

export const putDashboard = (orgId: string, id: string, body: { doc: DashboardDoc; version: number; name?: string; note?: string }) => api<Dashboard>(`${base(orgId)}/dashboards/${id}`, { method: 'PUT', body });

export function useSaveDashboard(orgId: string, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { doc: DashboardDoc; version: number; name?: string; note?: string }) => putDashboard(orgId, id, body),
    onSuccess: (data) => {
      qc.setQueryData(dashKeys.one(orgId, id), data);
      void qc.invalidateQueries({ queryKey: dashKeys.list(orgId) });
    },
  });
}

export function useVizAction(orgId: string, kind: VizKind) {
  const qc = useQueryClient();
  const done = () => qc.invalidateQueries({ queryKey: vizKeys.list(orgId, kind) });
  return {
    remove: useMutation({ mutationFn: (id: string) => api<OkResponse>(`${base(orgId)}/${kind}/${id}`, { method: 'DELETE' }), onSuccess: done }),
    duplicate: useMutation({ mutationFn: (id: string) => api<{ id: string }>(`${base(orgId)}/${kind}/${id}/duplicate`, { method: 'POST', body: {} }), onSuccess: done }),
    saveAsTemplate: useMutation({
      mutationFn: ({ id, name }: { id: string; name: string }) =>
        api<Template & { concreteSignals: number }>(`${base(orgId)}/dashboards/${id}/save-as-template`, { method: 'POST', body: { name } }),
      onSuccess: () => qc.invalidateQueries({ queryKey: ['org', orgId, 'templates'] }),
    }),
  };
}
export const useDashboardAction = (orgId: string) => useVizAction(orgId, 'dashboards');

/** An organization's own templates: rename / describe and delete (built-in ones are sealed). */
export function useTemplateActions(orgId: string) {
  const qc = useQueryClient();
  const done = () => qc.invalidateQueries({ queryKey: ['org', orgId, 'templates'] });
  return {
    update: useMutation({
      mutationFn: ({ id, ...body }: { id: string; name?: string; description?: string }) => api<Template>(`${base(orgId)}/templates/${id}`, { method: 'PATCH', body }),
      onSuccess: done,
    }),
    remove: useMutation({ mutationFn: (id: string) => api<OkResponse>(`${base(orgId)}/templates/${id}`, { method: 'DELETE' }), onSuccess: done }),
  };
}

/** Downloads a dashboard as a JSON file, with the names of the signals it uses (the ones the user sees). */
export const useExportDashboard = (orgId: string) =>
  useMutation({
    mutationFn: async (id: string) => {
      const [dashboard, signals] = await Promise.all([
        api<Dashboard>(`${base(orgId)}/dashboards/${id}`),
        api<{ items: Signal[] }>(`${base(orgId)}/signals`, { query: { status: 'all' } }).then((r) => r.items),
      ]);
      downloadText(dashboardFileName(dashboard.name), JSON.stringify(buildDashboardFile(orgId, dashboard, signals), null, 2), 'application/json', false);
    },
  });

/** What the share dialog needs of a dashboard or a schematic. */
export interface Shareable {
  id: string;
  name: string;
  sharing: Sharing | null;
  scopeBinding: ScopeBinding;
}

export function useShareViz(orgId: string, kind: VizKind, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Sharing & { scopeBinding?: ScopeBinding }) => api<Shareable>(`${base(orgId)}/${kind}/${id}/sharing`, { method: 'PUT', body }),
    onSuccess: (data) => {
      qc.setQueryData(vizKeys.one(orgId, kind, id), data);
      void qc.invalidateQueries({ queryKey: ['org', orgId] });
    },
  });
}

export const useVizAccess = (orgId: string, kind: VizKind, id: string, enabled: boolean) =>
  useQuery({
    queryKey: ['org', orgId, `${kind}-access`, id],
    queryFn: () => api<{ items: Array<{ userId: string; name: string; email: string; role: string; access: string; via: string }> }>(`${base(orgId)}/${kind}/${id}/access`).then((r) => r.items),
    enabled,
  });

/** Role bindings → signal ids for the selected well (doc 06 §4). */
export const useResolveBindings = (orgId: string, bindings: Array<{ refId: string; binding: unknown; vars: { wellId?: string } }>) =>
  useQuery({
    queryKey: ['org', orgId, 'resolve', bindings],
    queryFn: () => api<{ resolved: Record<string, string | null> }>(`${base(orgId)}/signals/resolve`, { method: 'POST', body: { bindings } }).then((r) => r.resolved),
    enabled: bindings.length > 0,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });

/** Every widget's history in ONE request (doc 06 §4). */
export const useBatchQuery = (orgId: string, queries: Array<{ key: string; spec: QuerySpec }>, refetchMs: number | false) =>
  useQuery({
    queryKey: ['org', orgId, 'batch', queries],
    queryFn: ({ signal }) => api<{ results: Record<string, QueryResult> }>(`${base(orgId)}/query/batch`, { method: 'POST', body: { queries }, signal }).then((r) => r.results),
    enabled: queries.length > 0,
    placeholderData: keepPreviousData,
    refetchInterval: refetchMs,
    staleTime: 2000,
    retry: false,
  });

export const useLiveTarget = (orgId: string, wellId: string | undefined) =>
  useQuery({
    queryKey: ['org', orgId, 'live-target', wellId],
    queryFn: () => api<{ dashboardId: string | null; source: string }>(`${base(orgId)}/live-target`, { query: { wellId } }),
    enabled: !!wellId,
  });

export type LiveLevel = 'org' | 'well' | 'test';
/** Sets (or, with `null`, removes) the dashboard Live monitoring opens: for the organization, a well or a test. */
export function useSetLiveDashboard(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ level, targetId, dashboardId }: { level: LiveLevel; targetId?: string; dashboardId: string | null }) =>
      level === 'org'
        ? api<unknown>(`${base(orgId)}/settings`, { method: 'PATCH', body: { settings: { defaultLiveDashboardId: dashboardId } } })
        : api<unknown>(`${base(orgId)}/${level === 'well' ? 'wells' : 'tests'}/${targetId}/default-dashboard`, { method: 'PUT', body: { dashboardId } }),
    onSuccess: (_data, { level }) => {
      void qc.invalidateQueries({ queryKey: ['org', orgId, 'live-target'] });
      if (level === 'org') void qc.invalidateQueries({ queryKey: orgKeys.context(orgId) });
    },
  });
}

export const useVersions = (orgId: string, id: string, enabled: boolean, kind: VizKind = 'dashboards') =>
  useQuery({
    queryKey: ['org', orgId, `${kind}-versions`, id],
    queryFn: () => api<{ items: Array<{ version: number; savedAt: string; savedByName: string; note: string | null }> }>(`${base(orgId)}/${kind}/${id}/versions`).then((r) => r.items),
    enabled,
  });

export function useRestoreVersion(orgId: string, id: string, kind: VizKind = 'dashboards') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (version: number) => api<unknown>(`${base(orgId)}/${kind}/${id}/versions/${version}/restore`, { method: 'POST' }),
    onSuccess: (data) => {
      qc.setQueryData(vizKeys.one(orgId, kind, id), data);
      void qc.invalidateQueries({ queryKey: ['org', orgId, `${kind}-versions`, id] });
    },
  });
}

export const saveDraft = (orgId: string, id: string, doc: DashboardDoc, baseVersion: number) =>
  api<OkResponse>(`${base(orgId)}/dashboards/${id}/draft`, { method: 'PUT', body: { doc, baseVersion } });
export const loadDraft = (orgId: string, id: string) => api<{ doc: DashboardDoc; baseVersion: number; updatedAt: string } | null>(`${base(orgId)}/dashboards/${id}/draft`);
export const dropDraft = (orgId: string, id: string) => api<OkResponse>(`${base(orgId)}/dashboards/${id}/draft`, { method: 'DELETE' });
